import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import {
    IconChevronLeft, IconMessage, IconTrash, IconExternalLink, IconDeviceMobile, IconClock, IconId, IconEye, IconX,
} from '@tabler/icons-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import customerService from '../../../services/sohozJobCustomerService';

// Each site's mark for the middle of its QR codes (public/images/…); a site without one gets a plain QR.
const SITE_MARKS = { teletalk: '/images/teletalk/teletalk-mark.webp' };

const QR_MARGIN = 1;

/** The QR's dark modules as one SVG path (horizontal runs), plus the full side length in modules. */
function qrPath(content, errorCorrectionLevel) {
    const { modules } = QRCode.create(content, { errorCorrectionLevel });
    const { size } = modules;
    let d = '';
    for (let row = 0; row < size; row++) {
        for (let col = 0; col < size;) {
            if (!modules.get(row, col)) { col++; continue; }
            let run = 1;
            while (col + run < size && modules.get(row, col + run)) run++;
            d += `M${col + QR_MARGIN} ${row + QR_MARGIN}h${run}v1h-${run}z`;
            col += run;
        }
    }
    return { d, full: size + 2 * QR_MARGIN };
}

/**
 * One step's QR, drawn as an inline <svg> straight from its content — "SMSTO:<number>:<text>" (cameras open it
 * as a ready-to-send SMS), "tel:<number>" or a link. No image data and nothing stored or uploaded. With a site
 * mark over its middle the QR uses error correction H (30%), so it still scans.
 */
function StepQr({ content, mark }) {
    const qr = useMemo(() => {
        try { return qrPath(content, mark ? 'H' : 'M'); } catch { return null; }
    }, [content, mark]);
    if (!qr) return null;
    return (
        <div className="relative w-full h-full">
            <svg viewBox={`0 0 ${qr.full} ${qr.full}`} shapeRendering="crispEdges" className="w-full h-full block" aria-hidden="true">
                <rect width={qr.full} height={qr.full} fill="#fff" />
                <path d={qr.d} fill="#000" />
            </svg>
            {/* Round badge with a thin white margin */}
            {mark && (
                <span className="absolute top-1/2 left-1/2 w-[21%] aspect-square -translate-x-1/2 -translate-y-1/2 p-[3px] rounded-full bg-white flex">
                    <img src={mark} alt="" className="w-full h-full rounded-full object-contain" />
                </span>
            )}
        </div>
    );
}

const PLACEHOLDER = /\{([a-z][a-z0-9_]{0,19})\}/g;

/** Is `value` valid for this input? The backend's fixed formats: "digits" within the length range. */
const inputOk = (input, value) =>
    input.format === 'digits' && /^\d+$/.test(value) && value.length >= input.min_length && value.length <= input.max_length;

/** The step's text with its {placeholders} filled, or null while an input it needs is missing / invalid. */
function fillText(text, inputs, values) {
    let missing = false;
    const filled = (text || '').replace(PLACEHOLDER, (_, key) => {
        const input = inputs.find((i) => i.key === key);
        const value = values[key] || '';
        if (!input || !inputOk(input, value)) { missing = true; return `<${input?.label || key}>`; }
        return value;
    });
    return { filled, missing };
}

/** What the QR encodes for a step (null while its text still needs an input). */
function stepContent(step, filled, missing) {
    if (step.type === 'sms') return missing ? null : `SMSTO:${step.target}:${filled}`;
    if (step.type === 'call') return `tel:${step.target}`;
    return step.target; // link
}

function InfoRow({ icon: Icon, label, children }) {
    return (
        <div className="flex items-center gap-2 text-sm min-w-0">
            <Icon size={15} className="text-zinc-400 flex-shrink-0" />
            <span className="text-zinc-500 dark:text-zinc-400 flex-shrink-0">{label}:</span>
            <span className="text-zinc-800 dark:text-zinc-100 font-medium truncate">{children}</span>
        </div>
    );
}

const steps = (m) => m.steps || [];
const inputs = (m) => m.inputs || [];
const usesInput = (step, input) => (step.text || '').includes(`{${input.key}}`);

/** "PIN" / any input still empty; null when every input has a value. */
function pendingInput(message) {
    return inputs(message).find((i) => !i.value) || null;
}

/**
 * One saved message, whatever site it came from (shown in the View modal): its details, a QR panel per step (in
 * order), and each input inside the panel of the step that uses it — Teletalk's PIN sits under the 2nd SMS's QR.
 * Typing an input redraws its step at once; Save stores the value.
 */
function MessageDetail({ customerId, message, onChange }) {
    const { t, currentLanguage } = useTranslation();
    const num = (n) => new Intl.NumberFormat(currentLanguage === 'bn' ? 'bn-BD' : 'en-US').format(n);
    const saved = Object.fromEntries(inputs(message).map((i) => [i.key, i.value || '']));
    const [values, setValues] = useState(saved);
    const [savingKey, setSavingKey] = useState(null);
    const mark = SITE_MARKS[message.site] || null;
    const isTeletalk = message.site === 'teletalk';
    // Teletalk's PIN gets its own wording; any other input uses the label it was saved with.
    const inputLabel = (input) => (isTeletalk && input.key === 'pin' ? t('sohozJob.messages.pinLabel') : input.label);

    const saveInput = async (input) => {
        const value = values[input.key] || '';
        if (value && !inputOk(input, value)) {
            notifications.show({ title: t('common.error'), message: t('sohozJob.messages.inputInvalid', { label: input.label, min: num(input.min_length), max: num(input.max_length) }), color: 'red' });
            return;
        }
        setSavingKey(input.key);
        try {
            onChange(await customerService.setMessageInput(customerId, message.id, input.key, value));
            notifications.show({ title: t('common.success'), message: t('sohozJob.messages.inputSaved', { label: input.label }), color: 'green' });
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('common.error'), color: 'red' });
        } finally {
            setSavingKey(null);
        }
    };

    const inputField = (input) => (
        <div key={input.key} className="w-full">
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">{inputLabel(input)}</label>
            <div className="flex gap-2">
                <input
                    value={values[input.key] || ''}
                    onChange={(e) => setValues((v) => ({ ...v, [input.key]: e.target.value.replace(/\D/g, '').slice(0, input.max_length) }))}
                    inputMode="numeric"
                    maxLength={input.max_length}
                    className="flex-1 min-w-0 h-10 px-3 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-mono tracking-widest text-center focus:outline-none focus:border-primary"
                />
                <button
                    onClick={() => saveInput(input)}
                    disabled={savingKey === input.key || (values[input.key] || '') === (saved[input.key] || '')}
                    className="px-4 h-10 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                    {t('sohozJob.messages.pinSave')}
                </button>
            </div>
        </div>
    );

    const stepPanel = (step) => {
        const { filled, missing } = fillText(step.text, inputs(message), values);
        const content = stepContent(step, filled, missing);
        const own = inputs(message).filter((i) => usesInput(step, i));
        const need = own.find((i) => !inputOk(i, values[i.key] || ''));
        return (
            <div key={step.position} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-zinc-100 dark:border-zinc-800 bg-zinc-50/60 dark:bg-zinc-800/30">
                <span className="text-[13px] font-semibold text-zinc-700 dark:text-zinc-200">
                    {t('sohozJob.messages.stepTitle', { n: num(step.position + 1), type: t(`sohozJob.messages.type_${step.type}`) })}
                </span>
                <div className="w-44 h-44 p-2 rounded-xl bg-white border border-zinc-200 dark:border-zinc-700 flex items-center justify-center">
                    {content
                        ? <StepQr content={content} mark={mark} />
                        : <span className="text-xs text-zinc-400 text-center px-3">{t('sohozJob.messages.inputFirst', { label: need ? inputLabel(need) : '' })}</span>}
                </div>
                {step.type === 'sms' && <code className="text-sm font-bold text-zinc-900 dark:text-zinc-100 tracking-wide break-all text-center">{filled}</code>}
                <span className="text-xs text-zinc-500 dark:text-zinc-400 break-all text-center">
                    {step.type === 'link'
                        ? (() => { try { return new URL(step.target).host; } catch { return step.target; } })()
                        : t(step.type === 'call' ? 'sohozJob.messages.callTo' : 'sohozJob.messages.sendTo', { to: step.target })}
                </span>
                {/* The inputs this step needs (Teletalk: the PIN) belong in its own panel */}
                {own.length > 0 && <div className="w-full pt-3 mt-1 border-t border-zinc-200/70 dark:border-zinc-700/60 space-y-3">{own.map(inputField)}</div>}
            </div>
        );
    };

    // Inputs no step mentions (none for Teletalk) go below the panels.
    const loose = inputs(message).filter((i) => !steps(message).some((st) => usesInput(st, i)));

    return (
        <div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-4">
                {message.reference && (
                    <InfoRow icon={IconId} label={message.reference.label === 'User ID' ? t('sohozJob.messages.userId') : message.reference.label}>
                        <span className="font-mono">{message.reference.value}</span>
                    </InfoRow>
                )}
                {message.deadline && <InfoRow icon={IconClock} label={t('sohozJob.messages.deadline')}>{message.deadline}</InfoRow>}
                {message.page_mobile && <InfoRow icon={IconDeviceMobile} label={t('sohozJob.messages.pageMobile')}>{message.page_mobile}</InfoRow>}
                {message.portal_url && (
                    <InfoRow icon={IconExternalLink} label={t('sohozJob.messages.portal')}>
                        <a href={message.portal_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            {(() => { try { return new URL(message.portal_url).host; } catch { return message.portal_url; } })()}
                        </a>
                    </InfoRow>
                )}
                <InfoRow icon={IconClock} label={t('sohozJob.messages.savedAt')}>{new Date(message.updatedAt || message.createdAt).toLocaleString()}</InfoRow>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 items-start">
                {steps(message).map(stepPanel)}
            </div>
            {loose.length > 0 && <div className="mt-3 space-y-3">{loose.map(inputField)}</div>}
            <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-4 text-center">
                {t(isTeletalk ? 'sohozJob.messages.scanHint' : 'sohozJob.messages.scanHintGeneric')}
            </p>
        </div>
    );
}

/** The View modal around MessageDetail: title (organisation + post) and a close button; scrolls on small screens. */
function MessageModal({ customerId, message, onClose, onChange }) {
    const { t } = useTranslation();
    return (
        <Dialog open={!!message} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent showClose={false} className="p-0 overflow-hidden flex flex-col font-hind" style={{ width: '880px', maxWidth: '95vw', maxHeight: '90vh' }}>
                {message && (
                    <>
                        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-zinc-100 dark:border-zinc-800">
                            <div className="min-w-0">
                                <DialogTitle className="text-[16px] font-semibold leading-snug truncate">
                                    {message.organization || t(`sohozJob.messages.site_${message.site}`, { defaultValue: message.site })}
                                </DialogTitle>
                                {message.post_name && <p className="text-sm text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{message.post_name}</p>}
                            </div>
                            <button
                                onClick={onClose}
                                aria-label={t('common.close')}
                                className="w-8 h-8 rounded-lg text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center flex-shrink-0 transition-colors"
                            >
                                <IconX size={17} />
                            </button>
                        </div>
                        <div className="overflow-y-auto p-5">
                            <MessageDetail key={message.id} customerId={customerId} message={message} onChange={onChange} />
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function CustomerMessages() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t, currentLanguage } = useTranslation();
    const num = (n) => new Intl.NumberFormat(currentLanguage === 'bn' ? 'bn-BD' : 'en-US').format(n);

    const [customer, setCustomer] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [viewId, setViewId] = useState(null);
    const viewing = messages.find((m) => m.id === viewId) || null;

    useEffect(() => {
        let alive = true;
        setLoading(true);
        customerService.listMessages(id)
            .then((res) => {
                if (!alive) return;
                setCustomer(res.customer);
                setMessages(res.messages || []);
            })
            .catch(() => notifications.show({ title: t('common.error'), message: t('sohozJob.messages.loadFailed'), color: 'red' }))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [id]);

    const handleDelete = async () => {
        try {
            await customerService.removeMessage(id, deleteTarget.id);
            setMessages((list) => list.filter((m) => m.id !== deleteTarget.id));
            if (viewId === deleteTarget.id) setViewId(null);
            notifications.show({ title: t('common.success'), message: t('sohozJob.messages.deleted'), color: 'green' });
            setDeleteTarget(null);
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('common.error'), color: 'red' });
        }
    };

    const name = customer?.applicant_name_bn || customer?.applicant_name_en;

    return (
        <div className="w-full font-hind space-y-5 sm:space-y-6 py-2 sm:py-4">
            <div>
                <button
                    onClick={() => navigate('/tools/form-auto-fillup')}
                    className="inline-flex items-center gap-1 text-[13px] font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors mb-3"
                >
                    <IconChevronLeft size={15} /> {t('sohozJob.backToList')}
                </button>
                <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 flex items-center justify-center flex-shrink-0">
                        <IconMessage size={22} stroke={1.7} />
                    </span>
                    <div className="min-w-0">
                        <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight truncate">
                            {t('sohozJob.messages.title')}{name ? ` · ${name}` : ''}
                        </h1>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
                            {customer?.mobile_number ? `${customer.mobile_number} · ` : ''}{t('sohozJob.messages.desc')}
                        </p>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <div className="w-6 h-6 border-2 border-zinc-300 border-t-primary rounded-full animate-spin" />
                </div>
            ) : messages.length === 0 ? (
                <div className="bg-white dark:bg-zinc-900 border border-dashed border-zinc-200 dark:border-zinc-700 rounded-2xl p-10 text-center">
                    <IconMessage size={28} className="mx-auto text-zinc-300 dark:text-zinc-600 mb-2" />
                    <p className="text-[15px] font-semibold text-zinc-700 dark:text-zinc-200">{t('sohozJob.messages.empty')}</p>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">{t('sohozJob.messages.emptyHint')}</p>
                </div>
            ) : (
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl shadow-sm overflow-hidden">
                    {/* Count bar */}
                    <div className="px-5 py-3 border-b border-zinc-100 dark:border-zinc-800">
                        <span className="text-[13px] font-semibold text-zinc-500 dark:text-zinc-400">
                            {t('sohozJob.messages.count', { count: num(messages.length) })}
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[12px] uppercase tracking-wide text-zinc-400 dark:text-zinc-500 border-b border-zinc-100 dark:border-zinc-800">
                                    <th className="px-5 py-3 font-semibold">{t('sohozJob.messages.colApplication')}</th>
                                    <th className="px-5 py-3 font-semibold">{t('sohozJob.messages.userId')}</th>
                                    <th className="px-5 py-3 font-semibold hidden md:table-cell">{t('sohozJob.messages.deadline')}</th>
                                    <th className="px-5 py-3 font-semibold">{t('sohozJob.messages.colStatus')}</th>
                                    <th className="px-5 py-3 font-semibold hidden lg:table-cell">{t('sohozJob.messages.savedAt')}</th>
                                    <th className="px-5 py-3 font-semibold text-right">{t('sohozJob.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {messages.map((m) => {
                                    const pending = pendingInput(m);
                                    return (
                                        <tr
                                            key={m.id}
                                            onClick={() => setViewId(m.id)}
                                            className="border-b border-zinc-50 dark:border-zinc-800/60 last:border-0 hover:bg-zinc-50/60 dark:hover:bg-zinc-800/40 transition-colors cursor-pointer"
                                        >
                                            <td className="px-5 py-3 max-w-[320px]">
                                                <p className="font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                                                    {m.organization || t(`sohozJob.messages.site_${m.site}`, { defaultValue: m.site })}
                                                </p>
                                                {m.post_name && <p className="text-xs text-zinc-400 dark:text-zinc-500 truncate">{m.post_name}</p>}
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap font-mono text-[13px] text-zinc-600 dark:text-zinc-300">{m.reference?.value || '—'}</td>
                                            <td className="px-5 py-3 whitespace-nowrap text-zinc-600 dark:text-zinc-300 hidden md:table-cell">{m.deadline || '—'}</td>
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                {pending ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11.5px] font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                                                        {t('sohozJob.messages.statusPending', { label: pending.label })}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11.5px] font-semibold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400">
                                                        {t('sohozJob.messages.statusReady')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-zinc-500 dark:text-zinc-400 hidden lg:table-cell">
                                                {new Date(m.updatedAt || m.createdAt).toLocaleString()}
                                            </td>
                                            <td className="px-5 py-3">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setViewId(m.id); }}
                                                        title={t('sohozJob.messages.view')}
                                                        aria-label={t('sohozJob.messages.view')}
                                                        className="w-8 h-8 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-blue-600 dark:text-blue-400 flex items-center justify-center hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                                    >
                                                        <IconEye size={16} />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(m); }}
                                                        title={t('common.delete')}
                                                        aria-label={t('common.delete')}
                                                        className="w-8 h-8 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-red-500 flex items-center justify-center hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                    >
                                                        <IconTrash size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <MessageModal
                customerId={id}
                message={viewing}
                onClose={() => setViewId(null)}
                onChange={(next) => setMessages((list) => list.map((x) => (x.id === next.id ? next : x)))}
            />

            <ConfirmDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title={t('sohozJob.messages.deleteTitle')}
                description={t('sohozJob.messages.deleteConfirm')}
            />
        </div>
    );
}
